// SalesReport.js — Fresh Hills Admin
// ✅ Day-wise chart updates with selected days (was hardcoded to 14)
// ✅ Apartment-wise sales tab with download
// ✅ Fridge-wise sales tab with product breakdown + download
import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import {
  CurrencyRupeeIcon, ShoppingBagIcon, ArrowTrendingUpIcon,
  CalendarIcon, ArrowDownTrayIcon, ArrowPathIcon,
  BuildingOfficeIcon, ArchiveBoxIcon,
} from '@heroicons/react/24/outline';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, BarElement, ArcElement, Title, Tooltip, Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend);

const API = process.env.REACT_APP_API_URL || 'https://freshhillsmilk.in/api';

const authHeaders = () => {
  const token = localStorage.getItem('adminToken') ||
                localStorage.getItem('fh_admin_token') ||
                localStorage.getItem('token') || '';
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
};

const fmt    = (n) => Number(n || 0).toLocaleString('en-IN');
const fmtRs  = (n) => `₹${fmt(n)}`;
const fmtDay = (iso) => new Date(iso).toLocaleDateString('en-IN', { day:'2-digit', month:'short', weekday:'short' });

// ─────────────────────────────────────────────────────────────────────────────
const SalesReport = () => {
  const [tab,           setTab]          = useState('overview');
  const [period,        setPeriod]       = useState('weekly');
  const [loading,       setLoading]      = useState(true);
  const [error,         setError]        = useState('');
  const [stats,         setStats]        = useState(null);
  const [trendData,     setTrendData]    = useState([]);
  const [productData,   setProductData]  = useState([]);
  const [channelData,   setChannelData]  = useState({ self_service: 0, delivery: 0 });
  const [daywiseData,   setDaywiseData]  = useState([]);
  const [daywiseDays,   setDaywiseDays]  = useState(30);
  const [allOrders,     setAllOrders]    = useState([]);
  const [aptData,       setAptData]      = useState([]);
  const [aptLoading,    setAptLoading]   = useState(false);
  const [fridgeData,    setFridgeData]   = useState([]);
  const [fridgeLoading, setFridgeLoading]= useState(false);
  const [expandedFridge,setExpandedFridge]= useState(null);
  const [fridgeProducts,setFridgeProducts]= useState({});

  const fetchProductData = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/sales/products?limit=10`, { headers: authHeaders() });
      const d = await r.json();
      if (d.success && d.products) return d.products.map(p => ({ name:p.product_name, units:p.units_sold, revenue:p.revenue }));
    } catch (e) {}
    return [];
  }, []);

  const fetchDaywise = useCallback(async (days) => {
    try {
      const r = await fetch(`${API}/admin/sales/daywise?days=${days}`, { headers: authHeaders() });
      const d = await r.json();
      if (d.success) return d.data || [];
    } catch (e) {}
    return [];
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/orders?limit=500&page=1`, { headers: authHeaders() });
      const d = await r.json();
      return d.orders || [];
    } catch { return []; }
  }, []);

  const fetchApartments = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/sales/apartments`, { headers: authHeaders() });
      const d = await r.json();
      if (d.success) return d.apartments || [];
    } catch (e) {}
    return [];
  }, []);

  const fetchFridges = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/sales/fridges`, { headers: authHeaders() });
      const d = await r.json();
      if (d.success) return d.fridges || [];
    } catch (e) {}
    return [];
  }, []);

  const fetchFridgeProducts = useCallback(async (fridge_uuid) => {
    if (fridgeProducts[fridge_uuid]) return;
    try {
      const r = await fetch(`${API}/admin/sales/fridge/${fridge_uuid}/products`, { headers: authHeaders() });
      const d = await r.json();
      if (d.success) setFridgeProducts(prev => ({ ...prev, [fridge_uuid]: d.products || [] }));
    } catch (e) {}
  }, [fridgeProducts]);

  const buildTrend = useCallback((daywise, p) => {
    const n   = p === 'weekly' ? 7 : 30;
    const cut = daywise.slice(-n);
    setTrendData(cut.map(d => ({ label: fmtDay(d.day), value: d.revenue })));
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [statsRes, products, daywise, orders] = await Promise.all([
        fetch(`${API}/admin/orders/stats`, { headers: authHeaders() }).then(r => r.json()),
        fetchProductData(), fetchDaywise(30), fetchOrders(),
      ]);
      if (statsRes.success) setStats(statsRes.stats);
      setProductData(products);
      setDaywiseData(daywise);
      setAllOrders(orders);
      const ch = { self_service: 0, delivery: 0 };
      orders.forEach(o => o.order_type === 'self_service' ? ch.self_service++ : ch.delivery++);
      setChannelData(ch);
      buildTrend(daywise, period);
    } catch (e) {
      setError('Failed to load sales data. Please try again.');
    } finally { setLoading(false); }
  }, [fetchProductData, fetchDaywise, fetchOrders, buildTrend, period]);

  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { if (daywiseData.length) buildTrend(daywiseData, period); }, [period, daywiseData, buildTrend]);

  // Reload day-wise when range changes
  useEffect(() => {
    if (tab !== 'daywise') return;
    fetchDaywise(daywiseDays).then(d => { setDaywiseData(d); buildTrend(d, period); });
  }, [daywiseDays]); // eslint-disable-line

  // Load apartment tab
  useEffect(() => {
    if (tab !== 'apartment') return;
    setAptLoading(true);
    fetchApartments().then(d => { setAptData(d); setAptLoading(false); });
  }, [tab, fetchApartments]);

  // Load fridge tab
  useEffect(() => {
    if (tab !== 'fridge') return;
    setFridgeLoading(true);
    fetchFridges().then(d => { setFridgeData(d); setFridgeLoading(false); });
  }, [tab, fetchFridges]);

  // Toggle fridge expand
  const toggleFridge = (uuid) => {
    if (expandedFridge === uuid) { setExpandedFridge(null); return; }
    setExpandedFridge(uuid);
    fetchFridgeProducts(uuid);
  };

  // ── CSV Exports ──────────────────────────────────────────────────────────────
  const downloadCSV = (rows, filename) => {
    const csv  = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const a    = document.createElement('a');
    a.href     = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
  };

  const exportCSV = () => {
    const today = new Date().toISOString().slice(0,10);
    if (tab === 'daywise' && daywiseData.length) {
      downloadCSV(
        [['Date','Total Orders','Revenue (₹)','QR Orders','Delivery Orders','Avg Order Value (₹)'],
         ...daywiseData.map(d => [d.day, d.total_orders, d.revenue, d.qr_orders||0, d.delivery_orders||0, d.avg_order_value])],
        `daywise_sales_${today}.csv`
      );
    } else if (tab === 'apartment' && aptData.length) {
      downloadCSV(
        [['Apartment','Total Orders','Total Revenue (₹)','Today Orders','Today Revenue (₹)','Active Fridges'],
         ...aptData.map(a => [a.apartment_name, a.total_orders, a.total_revenue, a.today_orders, a.today_revenue, a.active_fridges])],
        `apartment_sales_${today}.csv`
      );
    } else if (tab === 'fridge' && fridgeData.length) {
      downloadCSV(
        [['Apartment','Fridge ID','Location','Total Orders','Total Revenue (₹)','Today Orders','Today Revenue (₹)'],
         ...fridgeData.map(f => [f.apartment_name||'—', f.fridge_uuid, f.fridge_location||'—', f.total_orders, f.total_revenue, f.today_orders, f.today_revenue])],
        `fridge_sales_${today}.csv`
      );
    } else if (allOrders.length) {
      downloadCSV(
        [['Order #','Customer','Phone','Apartment','Fridge ID','Amount (₹)','Type','Status','Date & Time'],
         ...allOrders.map(o => [o.order_number, o.customer_name||'Guest', o.customer_phone||'—', o.apartment_name||'—', o.fridge_uuid||'—', o.total_amount, o.order_type, o.status, o.created_at ? new Date(o.created_at).toLocaleString('en-IN') : ''])],
        `orders_${today}.csv`
      );
    }
  };

  // ── Chart data ───────────────────────────────────────────────────────────────
  const lineData = {
    labels: trendData.map(d => d.label),
    datasets: [{ label:'Revenue (₹)', data:trendData.map(d => d.value),
      borderColor:'#059669', backgroundColor:'rgba(5,150,105,0.08)',
      tension:0.4, fill:true, pointBackgroundColor:'#059669',
      pointBorderColor:'white', pointBorderWidth:2, pointRadius:5 }],
  };

  // FIX: use daywiseDays not hardcoded 14
  const chartSlice  = daywiseData.slice(-daywiseDays);
  const daywiseBarData = {
    labels: chartSlice.map(d => fmtDay(d.day)),
    datasets: [{ label:'Revenue (₹)', data:chartSlice.map(d => d.revenue),
      backgroundColor: chartSlice.map((_, i) => i === chartSlice.length-1 ? '#059669' : 'rgba(5,150,105,0.55)'),
      borderRadius:6 }],
  };

  const barData = {
    labels: productData.map(p => p.name?.length > 14 ? p.name.slice(0,14)+'…' : p.name),
    datasets: [{ label:'Units Sold', data:productData.map(p => p.units),
      backgroundColor:['#059669','#0891b2','#7c3aed','#f59e0b','#10b981','#06b6d4','#8b5cf6','#ec4899','#f97316','#14b8a6'],
      borderRadius:8 }],
  };

  const totalCh   = channelData.self_service + channelData.delivery;
  const donutData = {
    labels: ['Self Service (QR)', 'Online Delivery'],
    datasets: [{ data:[channelData.self_service, channelData.delivery],
      backgroundColor:['#059669','#0891b2'], borderWidth:0 }],
  };

  const aptBarData = {
    labels: aptData.map(a => a.apartment_name?.length > 15 ? a.apartment_name.slice(0,15)+'…' : a.apartment_name),
    datasets: [
      { label:'Total Revenue (₹)', data:aptData.map(a => a.total_revenue), backgroundColor:'rgba(5,150,105,0.75)', borderRadius:6 },
      { label:"Today's Revenue (₹)", data:aptData.map(a => a.today_revenue), backgroundColor:'rgba(8,145,178,0.75)', borderRadius:6 },
    ],
  };

  const chartOpts = (yPrefix='₹') => ({
    responsive: true,
    plugins: { legend:{ display:false } },
    scales: {
      y: { grid:{ color:'#f1f5f9' }, ticks:{ color:'#64748b', font:{ family:'Nunito', weight:'700', size:11 }, callback: v => `${yPrefix}${Number(v).toLocaleString('en-IN')}` } },
      x: { grid:{ display:false }, ticks:{ color:'#64748b', font:{ family:'Nunito', weight:'700', size:10 } } },
    },
  });

  const chartOptsGrouped = () => ({
    responsive: true,
    plugins: { legend:{ display:true, position:'top', labels:{ font:{ family:'Nunito', weight:'700' }, color:'#374151', padding:16 } } },
    scales: {
      y: { grid:{ color:'#f1f5f9' }, ticks:{ color:'#64748b', font:{ family:'Nunito', weight:'700', size:11 }, callback: v => `₹${Number(v).toLocaleString('en-IN')}` } },
      x: { grid:{ display:false }, ticks:{ color:'#64748b', font:{ family:'Nunito', weight:'700', size:10 } } },
    },
  });

  const totalOrders   = stats?.total_orders   || 0;
  const totalRevenue  = stats?.total_revenue  || 0;
  const todayRevenue  = stats?.today_revenue  || 0;
  const todayOrders   = stats?.today_orders   || 0;
  const avgOrderValue = totalOrders > 0 ? (totalRevenue / totalOrders).toFixed(0) : 0;
  const maxProd       = productData.length ? Math.max(...productData.map(p => p.revenue)) : 1;

  const dwTotal   = daywiseData.reduce((a, d) => a + d.total_orders, 0);
  const dwRevenue = daywiseData.reduce((a, d) => a + d.revenue,      0);
  const dwBestDay = daywiseData.reduce((a, d) => (!a || d.revenue > a.revenue ? d : a), null);

  const aptTotals = {
    revenue: aptData.reduce((a,x) => a + x.total_revenue, 0),
    orders:  aptData.reduce((a,x) => a + x.total_orders,  0),
    today_r: aptData.reduce((a,x) => a + x.today_revenue, 0),
    today_o: aptData.reduce((a,x) => a + x.today_orders,  0),
  };

  const fridgeTotals = {
    revenue: fridgeData.reduce((a,x) => a + x.total_revenue, 0),
    orders:  fridgeData.reduce((a,x) => a + x.total_orders,  0),
    today_r: fridgeData.reduce((a,x) => a + x.today_revenue, 0),
    today_o: fridgeData.reduce((a,x) => a + x.today_orders,  0),
  };

  const TABS = [
    { key:'overview',  label:'📈 Overview'    },
    { key:'daywise',   label:'📅 Day-wise'    },
    { key:'apartment', label:'🏘️ Apartment'   },
    { key:'fridge',    label:'🧊 Fridge'       },
  ];

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:'Nunito',sans-serif;}

    .sr-hdr{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:1.5rem;flex-wrap:wrap;gap:1rem;}
    .sr-title{font-size:1.55rem;font-weight:900;color:#111827;}
    .sr-sub{font-size:0.85rem;color:#64748b;font-weight:600;margin-top:0.15rem;}
    .sr-ctrl{display:flex;align-items:center;gap:0.65rem;flex-wrap:wrap;}
    .sr-pg{display:flex;background:#f1f5f9;border-radius:9999px;padding:0.22rem;}
    .sr-pb{padding:0.38rem .9rem;border-radius:9999px;border:none;cursor:pointer;font-weight:700;font-size:0.8rem;color:#64748b;background:none;font-family:'Nunito',sans-serif;transition:all 0.2s;}
    .sr-pb.act{background:white;color:#059669;box-shadow:0 2px 8px rgba(0,0,0,.08);}
    .sr-tabs{display:flex;gap:0.4rem;margin-bottom:1.5rem;background:#f1f5f9;border-radius:14px;padding:0.25rem;width:fit-content;flex-wrap:wrap;}
    .sr-tab{padding:0.48rem 1.1rem;border-radius:10px;border:none;cursor:pointer;font-weight:700;font-size:0.82rem;color:#64748b;background:none;font-family:'Nunito',sans-serif;transition:all 0.2s;white-space:nowrap;}
    .sr-tab.act{background:white;color:#059669;box-shadow:0 2px 8px rgba(0,0,0,.09);}
    .sr-exp{display:flex;align-items:center;gap:0.38rem;padding:0.42rem .95rem;border-radius:9999px;background:#ecfdf5;color:#059669;border:1.5px solid #a7f3d0;font-weight:700;font-size:0.8rem;cursor:pointer;transition:all 0.2s;font-family:'Nunito',sans-serif;}
    .sr-exp svg{width:0.85rem;height:0.85rem;}
    .sr-exp:hover{background:#059669;color:white;border-color:#059669;}
    .sr-ref{display:flex;align-items:center;gap:0.35rem;padding:0.42rem .95rem;border-radius:9999px;background:white;color:#374151;border:1.5px solid #e5e7eb;font-weight:700;font-size:0.8rem;cursor:pointer;transition:all 0.2s;font-family:'Nunito',sans-serif;}
    .sr-ref svg{width:0.82rem;height:0.82rem;}
    .sr-ref:hover{border-color:#a7f3d0;color:#059669;}
    .sr-stats{display:grid;grid-template-columns:1fr;gap:1.1rem;margin-bottom:1.75rem;}
    @media(min-width:560px){.sr-stats{grid-template-columns:repeat(2,1fr);}}
    @media(min-width:1024px){.sr-stats{grid-template-columns:repeat(4,1fr);}}
    .sr-sc{background:white;border-radius:1.25rem;padding:1.25rem 1.35rem;border:1.5px solid;box-shadow:0 3px 10px rgba(0,0,0,.04);transition:all .3s;}
    .sr-sc:hover{transform:translateY(-3px);box-shadow:0 10px 20px rgba(0,0,0,.07);}
    .sr-sc-lbl{font-size:0.75rem;font-weight:700;color:#64748b;margin-bottom:0.35rem;text-transform:uppercase;letter-spacing:.3px;}
    .sr-sc-val{font-size:1.65rem;font-weight:900;color:#111827;margin-bottom:0.2rem;}
    .sr-sc-sub{font-size:0.7rem;font-weight:600;}
    .sr-grid2{display:grid;grid-template-columns:1fr;gap:1.35rem;margin-bottom:1.35rem;}
    @media(min-width:1024px){.sr-grid2{grid-template-columns:2fr 1fr;}}
    .sr-row2{display:grid;grid-template-columns:1fr;gap:1.35rem;margin-bottom:1.35rem;}
    @media(min-width:768px){.sr-row2{grid-template-columns:1fr 1fr;}}
    .sr-card{background:white;border-radius:1.35rem;padding:1.35rem;border:1px solid #f1f5f9;box-shadow:0 3px 14px rgba(0,0,0,.04);}
    .sr-ct{font-size:0.9rem;font-weight:800;color:#111827;margin-bottom:1.15rem;display:flex;align-items:center;gap:0.45rem;}
    .sr-ct svg{width:0.95rem;height:0.95rem;color:#059669;}
    .sr-pr{display:flex;align-items:center;gap:.85rem;padding:.65rem 0;border-bottom:1px solid #f8fafc;}
    .sr-pr:last-child{border-bottom:none;}
    .sr-pr-rk{width:26px;height:26px;border-radius:50%;background:#ecfdf5;color:#059669;font-weight:900;font-size:0.75rem;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
    .sr-pr-nm{font-weight:700;color:#111827;font-size:0.85rem;flex:1;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
    .sr-pr-bw{width:70px;height:5px;background:#f1f5f9;border-radius:9999px;overflow:hidden;flex-shrink:0;}
    .sr-pr-b{height:100%;background:linear-gradient(90deg,#10b981,#0891b2);border-radius:9999px;}
    .sr-pr-u{font-size:0.72rem;color:#64748b;font-weight:600;width:65px;text-align:right;flex-shrink:0;}
    .sr-pr-rv{font-weight:800;color:#059669;font-size:0.85rem;width:75px;text-align:right;flex-shrink:0;}

    /* Day-wise */
    .dw-toolbar{display:flex;align-items:center;gap:0.75rem;margin-bottom:1.25rem;flex-wrap:wrap;}
    .dw-range{display:flex;background:#f1f5f9;border-radius:9999px;padding:0.22rem;}
    .dw-rb{padding:0.35rem .8rem;border-radius:9999px;border:none;cursor:pointer;font-weight:700;font-size:0.78rem;color:#64748b;background:none;font-family:'Nunito',sans-serif;transition:all 0.2s;}
    .dw-rb.act{background:white;color:#059669;box-shadow:0 2px 6px rgba(0,0,0,.08);}
    .dw-sum{display:grid;grid-template-columns:repeat(3,1fr);gap:1rem;margin-bottom:1.5rem;}
    .dw-sum-card{background:white;border-radius:1.1rem;padding:1rem 1.25rem;border:1.5px solid #e5e7eb;text-align:center;}
    .dw-sum-val{font-size:1.4rem;font-weight:900;}
    .dw-sum-lbl{font-size:0.72rem;font-weight:700;color:#64748b;margin-top:0.15rem;}

    /* Tables */
    .tbl-wrap{background:white;border-radius:1.25rem;overflow:hidden;border:1px solid #f1f5f9;box-shadow:0 3px 14px rgba(0,0,0,.04);margin-bottom:1.35rem;overflow-x:auto;}
    .tbl{width:100%;border-collapse:collapse;min-width:600px;}
    .tbl-th{padding:0.85rem 1.1rem;text-align:left;font-size:0.72rem;font-weight:800;color:#64748b;text-transform:uppercase;letter-spacing:.5px;background:#f8fafc;border-bottom:1px solid #f1f5f9;white-space:nowrap;}
    .tbl-td{padding:0.85rem 1.1rem;border-bottom:1px solid #f8fafc;font-size:0.875rem;color:#374151;vertical-align:middle;}
    .tbl-tr:last-child .tbl-td{border-bottom:none;}
    .tbl-tr:hover .tbl-td{background:#fafbfc;}
    .tbl-tr.today .tbl-td{background:#f0fdf4;}
    .tbl-rev{font-weight:900;color:#059669;}
    .tbl-ord{font-weight:700;color:#2563eb;}
    .tbl-bar-wrap{width:80px;height:6px;background:#f1f5f9;border-radius:9999px;overflow:hidden;display:inline-block;vertical-align:middle;margin-left:0.5rem;}
    .tbl-bar{height:100%;background:linear-gradient(90deg,#10b981,#059669);border-radius:9999px;}
    .tbl-expand-btn{background:#f0fdf4;color:#059669;border:1px solid #a7f3d0;border-radius:6px;padding:0.22rem 0.6rem;font-size:0.72rem;font-weight:700;cursor:pointer;font-family:'Nunito',sans-serif;}
    .tbl-expand-btn:hover{background:#059669;color:white;}
    .tbl-sub{background:#f8fafc;border-top:none;}
    .tbl-sub .tbl-td{padding:0.65rem 1.1rem;font-size:0.82rem;}
    .prod-chip{display:inline-flex;align-items:center;gap:0.3rem;background:#ecfdf5;color:#059669;border-radius:6px;padding:0.18rem 0.5rem;font-size:0.72rem;font-weight:700;margin:0.15rem;}

    /* Apt/Fridge summary strip */
    .sum-strip{display:grid;grid-template-columns:repeat(2,1fr);gap:1rem;margin-bottom:1.5rem;}
    @media(min-width:768px){.sum-strip{grid-template-columns:repeat(4,1fr);}}
    .sum-strip-card{background:white;border-radius:1rem;padding:1rem 1.1rem;border:1.5px solid;text-align:center;box-shadow:0 2px 8px rgba(0,0,0,.04);}
    .sum-strip-val{font-size:1.35rem;font-weight:900;}
    .sum-strip-lbl{font-size:0.7rem;font-weight:700;color:#64748b;margin-top:0.12rem;}

    .sr-empty{text-align:center;padding:2rem 1rem;color:#9ca3af;font-size:.85rem;font-weight:600;}
    .sr-empty-ico{font-size:2rem;margin-bottom:.5rem;}
    .sr-err{background:#fef2f2;border:1px solid #fecaca;border-radius:1rem;padding:1rem 1.25rem;color:#dc2626;font-weight:600;font-size:.85rem;margin-bottom:1rem;}
    @keyframes sk{0%,100%{opacity:1}50%{opacity:.35}}
    .sk{animation:sk 1.4s ease-in-out infinite;background:#f3f4f6;border-radius:.75rem;}
    .sk-card{height:110px;}
    .sk-chart{height:260px;}
    @keyframes spin{to{transform:rotate(360deg)}}
  `;

  const statCards = [
    { label:"Today's Revenue", value:fmtRs(todayRevenue),  sub:`${todayOrders} orders today`,      color:'#059669', border:'#a7f3d0' },
    { label:'Total Orders',    value:fmt(totalOrders),      sub:`${stats?.completed||0} completed`, color:'#2563eb', border:'#bfdbfe' },
    { label:'Avg Order Value', value:fmtRs(avgOrderValue),  sub:'Per transaction',                  color:'#7c3aed', border:'#ddd6fe' },
    { label:'Total Revenue',   value:fmtRs(totalRevenue),   sub:'All time',                         color:'#d97706', border:'#fde68a' },
  ];

  return (
    <AdminLayout>
      <style>{css}</style>

      {/* Header */}
      <div className="sr-hdr">
        <div>
          <div className="sr-title">📊 Sales Report</div>
          <div className="sr-sub">Real-time revenue, orders and product performance</div>
        </div>
        <div className="sr-ctrl">
          {tab === 'overview' && (
            <div className="sr-pg">
              {['weekly','monthly'].map(p => (
                <button key={p} className={`sr-pb${period===p?' act':''}`} onClick={() => setPeriod(p)}>
                  {p.charAt(0).toUpperCase()+p.slice(1)}
                </button>
              ))}
            </div>
          )}
          <button className="sr-ref" onClick={load} disabled={loading}>
            <ArrowPathIcon style={loading?{animation:'spin 1s linear infinite'}:{}}/> Refresh
          </button>
          <button className="sr-exp" onClick={exportCSV}>
            <ArrowDownTrayIcon/> Export CSV
          </button>
        </div>
      </div>

      {error && <div className="sr-err">⚠️ {error}</div>}

      {/* Tabs */}
      <div className="sr-tabs">
        {TABS.map(t => (
          <button key={t.key} className={`sr-tab${tab===t.key?' act':''}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ══════════════════════ OVERVIEW TAB ══════════════════════ */}
      {tab === 'overview' && (
        <>
          <div className="sr-stats">
            {loading ? Array.from({length:4}).map((_,i) => <div key={i} className="sk sk-card"/>)
              : statCards.map(s => (
                <div key={s.label} className="sr-sc" style={{ borderColor:s.border }}>
                  <div className="sr-sc-lbl">{s.label}</div>
                  <div className="sr-sc-val">{s.value}</div>
                  <div className="sr-sc-sub" style={{ color:s.color }}>{s.sub}</div>
                </div>
              ))}
          </div>

          <div className="sr-grid2">
            <div className="sr-card">
              <div className="sr-ct"><ArrowTrendingUpIcon/> Revenue Trend ({period})</div>
              {loading ? <div className="sk sk-chart"/>
                : trendData.every(d => d.value === 0)
                  ? <div className="sr-empty"><div className="sr-empty-ico">📈</div>No revenue data yet</div>
                  : <Line data={lineData} options={chartOpts()}/>}
            </div>
            <div className="sr-card">
              <div className="sr-ct"><ShoppingBagIcon/> Orders by Channel</div>
              {loading ? <div className="sk sk-chart"/>
                : totalCh === 0
                  ? <div className="sr-empty"><div className="sr-empty-ico">🛒</div>No orders yet</div>
                  : <>
                      <Doughnut data={donutData} options={{ responsive:true, plugins:{ legend:{ position:'bottom', labels:{ font:{ family:'Nunito', weight:'700' }, color:'#374151', padding:16 } } } }}/>
                      <div style={{display:'flex',justifyContent:'center',gap:'1.5rem',marginTop:'.75rem',fontSize:'.78rem',fontWeight:'700',color:'#374151'}}>
                        <span>🟢 QR: {channelData.self_service}</span>
                        <span>🔵 Delivery: {channelData.delivery}</span>
                      </div>
                    </>}
            </div>
          </div>

          <div className="sr-row2">
            <div className="sr-card">
              <div className="sr-ct"><ShoppingBagIcon/> Top Products — Units Sold</div>
              {loading ? <div className="sk sk-chart"/>
                : productData.length === 0
                  ? <div className="sr-empty"><div className="sr-empty-ico">📦</div>No product sales data</div>
                  : <Bar data={barData} options={chartOpts('')}/>}
            </div>
            <div className="sr-card">
              <div className="sr-ct"><CurrencyRupeeIcon/> Top Products — Revenue</div>
              {loading ? <div className="sk sk-chart"/>
                : productData.length === 0
                  ? <div className="sr-empty"><div className="sr-empty-ico">📦</div>No product sales data</div>
                  : productData.map((p,i) => (
                    <div key={p.name} className="sr-pr">
                      <div className="sr-pr-rk">{i+1}</div>
                      <div className="sr-pr-nm" title={p.name}>{p.name}</div>
                      <div className="sr-pr-bw"><div className="sr-pr-b" style={{width:`${Math.round((p.revenue/maxProd)*100)}%`}}/></div>
                      <div className="sr-pr-u">{p.units} units</div>
                      <div className="sr-pr-rv">{fmtRs(p.revenue)}</div>
                    </div>
                  ))}
            </div>
          </div>

          {!loading && stats && (
            <div className="sr-card" style={{marginBottom:'1.35rem'}}>
              <div className="sr-ct"><CalendarIcon/> Order Status Summary</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))',gap:'.85rem'}}>
                {[{label:'Pending',value:stats.pending||0,color:'#d97706',bg:'#fef3c7'},{label:'Confirmed',value:stats.confirmed||0,color:'#2563eb',bg:'#eff6ff'},{label:'Completed',value:stats.completed||0,color:'#059669',bg:'#ecfdf5'},{label:'Cancelled',value:stats.cancelled||0,color:'#dc2626',bg:'#fef2f2'}]
                  .map(s => (
                    <div key={s.label} style={{background:s.bg,borderRadius:'.85rem',padding:'.75rem 1rem',textAlign:'center'}}>
                      <div style={{fontSize:'1.5rem',fontWeight:'900',color:s.color}}>{s.value}</div>
                      <div style={{fontSize:'.72rem',fontWeight:'700',color:'#6b7280',marginTop:'.15rem'}}>{s.label}</div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════════════════ DAY-WISE TAB ══════════════════════ */}
      {tab === 'daywise' && (
        <>
          <div className="dw-toolbar">
            <span style={{fontWeight:700,fontSize:'0.85rem',color:'#374151'}}>Show last:</span>
            <div className="dw-range">
              {[7,14,30,60,90].map(d => (
                <button key={d} className={`dw-rb${daywiseDays===d?' act':''}`} onClick={() => setDaywiseDays(d)}>
                  {d} days
                </button>
              ))}
            </div>
          </div>

          <div className="dw-sum">
            <div className="dw-sum-card" style={{borderColor:'#a7f3d0'}}>
              <div className="dw-sum-val" style={{color:'#059669'}}>{fmtRs(dwRevenue)}</div>
              <div className="dw-sum-lbl">Total Revenue</div>
            </div>
            <div className="dw-sum-card" style={{borderColor:'#bfdbfe'}}>
              <div className="dw-sum-val" style={{color:'#2563eb'}}>{dwTotal}</div>
              <div className="dw-sum-lbl">Total Orders</div>
            </div>
            <div className="dw-sum-card" style={{borderColor:'#fde68a'}}>
              <div className="dw-sum-val" style={{color:'#d97706'}}>{dwBestDay ? fmtDay(dwBestDay.day) : '—'}</div>
              <div className="dw-sum-lbl">Best Day {dwBestDay ? `(${fmtRs(dwBestDay.revenue)})` : ''}</div>
            </div>
          </div>

          {/* FIX: chart title and data now use daywiseDays */}
          <div className="sr-card" style={{marginBottom:'1.35rem'}}>
            <div className="sr-ct"><ArrowTrendingUpIcon/> Daily Revenue (last {daywiseDays} days)</div>
            {loading ? <div className="sk sk-chart"/>
              : chartSlice.length === 0
                ? <div className="sr-empty"><div className="sr-empty-ico">📅</div>No day-wise data yet</div>
                : <Bar data={daywiseBarData} options={chartOpts()}/>}
          </div>

          <div className="tbl-wrap">
            {loading ? <div style={{padding:'2rem',textAlign:'center',color:'#94a3b8',fontWeight:700}}>Loading…</div>
              : daywiseData.length === 0
                ? <div className="sr-empty"><div className="sr-empty-ico">📅</div>No sales data found</div>
                : (
                  <table className="tbl">
                    <thead>
                      <tr>{['Date','Orders','Revenue (₹)','QR Orders','Delivery','Avg Order Value'].map(h => <th key={h} className="tbl-th">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {[...daywiseData].reverse().map(d => {
                        const today = new Date().toISOString().slice(0,10);
                        const isToday = d.day === today;
                        const maxRev = Math.max(...daywiseData.map(x => x.revenue)) || 1;
                        return (
                          <tr key={d.day} className={`tbl-tr${isToday?' today':''}`}>
                            <td className="tbl-td">
                              <strong>{fmtDay(d.day)}</strong>
                              {isToday && <span style={{marginLeft:'0.5rem',fontSize:'0.7rem',background:'#ecfdf5',color:'#059669',padding:'0.1rem 0.45rem',borderRadius:'9999px',fontWeight:800}}>Today</span>}
                            </td>
                            <td className="tbl-td"><span className="tbl-ord">{d.total_orders}</span></td>
                            <td className="tbl-td">
                              <span className="tbl-rev">{fmtRs(d.revenue)}</span>
                              <span className="tbl-bar-wrap"><span className="tbl-bar" style={{width:`${Math.round((d.revenue/maxRev)*100)}%`}}/></span>
                            </td>
                            <td className="tbl-td">{d.qr_orders||0}</td>
                            <td className="tbl-td">{d.delivery_orders||0}</td>
                            <td className="tbl-td">{fmtRs(d.avg_order_value)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
          </div>
        </>
      )}

      {/* ══════════════════════ APARTMENT TAB ══════════════════════ */}
      {tab === 'apartment' && (
        <>
          {/* Summary strip */}
          <div className="sum-strip">
            {[
              {label:'Total Revenue',    value:fmtRs(aptTotals.revenue),  color:'#059669', border:'#a7f3d0'},
              {label:'Total Orders',     value:fmt(aptTotals.orders),      color:'#2563eb', border:'#bfdbfe'},
              {label:"Today's Revenue",  value:fmtRs(aptTotals.today_r),  color:'#7c3aed', border:'#ddd6fe'},
              {label:"Today's Orders",   value:fmt(aptTotals.today_o),    color:'#d97706', border:'#fde68a'},
            ].map(s => (
              <div key={s.label} className="sum-strip-card" style={{borderColor:s.border}}>
                <div className="sum-strip-val" style={{color:s.color}}>{s.value}</div>
                <div className="sum-strip-lbl">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Bar chart */}
          {!aptLoading && aptData.length > 0 && (
            <div className="sr-card" style={{marginBottom:'1.35rem'}}>
              <div className="sr-ct"><BuildingOfficeIcon/> Apartment Revenue Comparison</div>
              <Bar data={aptBarData} options={chartOptsGrouped()}/>
            </div>
          )}

          {/* Table */}
          <div className="tbl-wrap">
            {aptLoading ? <div style={{padding:'2rem',textAlign:'center',color:'#94a3b8',fontWeight:700}}>Loading…</div>
              : aptData.length === 0
                ? <div className="sr-empty"><div className="sr-empty-ico">🏘️</div>No apartment data found</div>
                : (
                  <table className="tbl">
                    <thead>
                      <tr>{['Apartment','Total Orders','Total Revenue','Today Orders',"Today's Revenue",'Active Fridges'].map(h => <th key={h} className="tbl-th">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {aptData.map(a => {
                        const maxR = Math.max(...aptData.map(x => x.total_revenue)) || 1;
                        return (
                          <tr key={a.apartment_id} className="tbl-tr">
                            <td className="tbl-td"><strong>{a.apartment_name}</strong></td>
                            <td className="tbl-td"><span className="tbl-ord">{a.total_orders}</span></td>
                            <td className="tbl-td">
                              <span className="tbl-rev">{fmtRs(a.total_revenue)}</span>
                              <span className="tbl-bar-wrap"><span className="tbl-bar" style={{width:`${Math.round((a.total_revenue/maxR)*100)}%`}}/></span>
                            </td>
                            <td className="tbl-td"><span className="tbl-ord">{a.today_orders}</span></td>
                            <td className="tbl-td"><span className="tbl-rev">{fmtRs(a.today_revenue)}</span></td>
                            <td className="tbl-td">{a.active_fridges}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
          </div>
        </>
      )}

      {/* ══════════════════════ FRIDGE TAB ══════════════════════ */}
      {tab === 'fridge' && (
        <>
          <div className="sum-strip">
            {[
              {label:'Total Revenue',    value:fmtRs(fridgeTotals.revenue), color:'#059669', border:'#a7f3d0'},
              {label:'Total Orders',     value:fmt(fridgeTotals.orders),     color:'#2563eb', border:'#bfdbfe'},
              {label:"Today's Revenue",  value:fmtRs(fridgeTotals.today_r), color:'#7c3aed', border:'#ddd6fe'},
              {label:'Total Fridges',    value:fridgeData.length,            color:'#d97706', border:'#fde68a'},
            ].map(s => (
              <div key={s.label} className="sum-strip-card" style={{borderColor:s.border}}>
                <div className="sum-strip-val" style={{color:s.color}}>{s.value}</div>
                <div className="sum-strip-lbl">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="tbl-wrap">
            {fridgeLoading ? <div style={{padding:'2rem',textAlign:'center',color:'#94a3b8',fontWeight:700}}>Loading…</div>
              : fridgeData.length === 0
                ? <div className="sr-empty"><div className="sr-empty-ico">🧊</div>No fridge data found</div>
                : (
                  <table className="tbl">
                    <thead>
                      <tr>{['Apartment','Fridge ID','Location','Total Orders','Total Revenue',"Today's Orders","Today's Revenue",'Details'].map(h => <th key={h} className="tbl-th">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {fridgeData.map(f => {
                        const maxR = Math.max(...fridgeData.map(x => x.total_revenue)) || 1;
                        const isExpanded = expandedFridge === f.fridge_uuid;
                        const prods = fridgeProducts[f.fridge_uuid] || [];
                        return (
                          <React.Fragment key={f.fridge_uuid}>
                            <tr className="tbl-tr">
                              <td className="tbl-td"><strong>{f.apartment_name||'—'}</strong></td>
                              <td className="tbl-td"><code style={{background:'#f1f5f9',padding:'0.15rem 0.4rem',borderRadius:4,fontSize:'0.78rem'}}>{f.fridge_uuid}</code></td>
                              <td className="tbl-td" style={{maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={f.fridge_location}>{f.fridge_location||'—'}</td>
                              <td className="tbl-td"><span className="tbl-ord">{f.total_orders}</span></td>
                              <td className="tbl-td">
                                <span className="tbl-rev">{fmtRs(f.total_revenue)}</span>
                                <span className="tbl-bar-wrap"><span className="tbl-bar" style={{width:`${Math.round((f.total_revenue/maxR)*100)}%`}}/></span>
                              </td>
                              <td className="tbl-td"><span className="tbl-ord">{f.today_orders}</span></td>
                              <td className="tbl-td"><span className="tbl-rev">{fmtRs(f.today_revenue)}</span></td>
                              <td className="tbl-td">
                                <button className="tbl-expand-btn" onClick={() => toggleFridge(f.fridge_uuid)}>
                                  {isExpanded ? '▲ Hide' : '▼ Products'}
                                </button>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className="tbl-sub">
                                <td colSpan={8} className="tbl-td" style={{paddingTop:0}}>
                                  {prods.length === 0
                                    ? <div style={{color:'#9ca3af',fontWeight:600,padding:'0.5rem 0'}}>Loading product data…</div>
                                    : (
                                      <table style={{width:'100%',borderCollapse:'collapse',marginTop:'0.5rem'}}>
                                        <thead>
                                          <tr>
                                            {['Product','Total Added','Sold','Remaining','Revenue'].map(h => (
                                              <th key={h} style={{padding:'0.5rem 0.75rem',textAlign:'left',fontSize:'0.68rem',fontWeight:800,color:'#64748b',textTransform:'uppercase',letterSpacing:'.5px',borderBottom:'1px solid #e5e7eb'}}>{h}</th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {prods.map(p => (
                                            <tr key={p.product_id}>
                                              <td style={{padding:'0.5rem 0.75rem',fontWeight:700,fontSize:'0.82rem'}}>{p.product_name}</td>
                                              <td style={{padding:'0.5rem 0.75rem',fontSize:'0.82rem',color:'#374151'}}>{p.total_stock_added}</td>
                                              <td style={{padding:'0.5rem 0.75rem',fontSize:'0.82rem'}}>
                                                <span style={{color:p.quantity_sold > 0 ? '#059669' : '#9ca3af',fontWeight:700}}>{p.quantity_sold}</span>
                                              </td>
                                              <td style={{padding:'0.5rem 0.75rem',fontSize:'0.82rem'}}>
                                                <span style={{color:p.remaining_stock === 0 ? '#dc2626' : p.remaining_stock <= 5 ? '#d97706' : '#059669',fontWeight:700}}>{p.remaining_stock}</span>
                                              </td>
                                              <td style={{padding:'0.5rem 0.75rem',fontWeight:800,color:'#059669',fontSize:'0.82rem'}}>{fmtRs(p.revenue)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default SalesReport;
